import { ResponsivePie } from '@nivo/pie'

import { formatCount } from 'src/pages/AnalyticsPage/components/heroStatHelpers'

import {
  BOOKING_OUTCOME_COLORS,
  type BookingOutcomeChartData,
} from './bookingOutcomeChartTheme'

export const BOOKING_OUTCOME_INFO_TEXT =
  'Every meeting booked, split by outcome. This counts bookings, not people — an applicant who books more than once is counted each time — so the total can be higher than the funnel’s Booked count.'

const OUTCOME_SEGMENTS = [
  {
    label: 'Completed',
    valueKey: 'accepted' as const,
    color: BOOKING_OUTCOME_COLORS.completed,
  },
  {
    label: 'No-show',
    valueKey: 'noShow' as const,
    color: BOOKING_OUTCOME_COLORS.noShow,
  },
  {
    label: 'Cancelled',
    valueKey: 'cancelled' as const,
    color: BOOKING_OUTCOME_COLORS.cancelled,
  },
]

const percentOf = (value: number, total: number): string => {
  if (total === 0) return '0%'
  return `${Math.round((value / total) * 100)}%`
}

const BookingOutcomeChart = ({ data }: { data: BookingOutcomeChartData }) => {
  const isEmpty = data.total === 0

  const pieData = OUTCOME_SEGMENTS.map((segment) => ({
    id: segment.label,
    label: segment.label,
    value: data[segment.valueKey],
    color: segment.color,
  })).filter((segment) => segment.value > 0)

  if (isEmpty) {
    return (
      <div
        className="flex h-[350px] flex-col items-center justify-center text-sm font-medium text-doubleNickel-gray-500"
        data-testid="booking-outcome-chart-empty"
      >
        No bookings in this period
      </div>
    )
  }

  return (
    <div className="pt-4" data-testid="booking-outcome-chart">
      <div className="relative h-[320px]">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-3xl font-semibold text-doubleNickel-gray-800">
              {formatCount(data.total)}
            </p>
            <p className="text-sm text-doubleNickel-gray-500">Total Bookings</p>
          </div>
        </div>
        <ResponsivePie
          data={pieData}
          margin={{ top: 28, bottom: 10, right: 10, left: 10 }}
          startAngle={-120}
          innerRadius={0.62}
          padAngle={2}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          colors={{ datum: 'data.color' }}
          enableArcLinkLabels
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          arcLabel={(e) => percentOf(e.value, data.total)}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLinkLabelsDiagonalLength={5}
          arcLinkLabelsStraightLength={5}
          arcLabelsTextColor={{
            from: 'color',
            modifiers: [['darker', 2]],
          }}
        />
      </div>
    </div>
  )
}

export default BookingOutcomeChart
